import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsMoreDropdownOpen(false);
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const mainNavItems = [
        { name: 'Home', path: '/' },
        { name: 'About', path: '/about' },
        { name: 'Academics', path: '/academics' },
        { name: 'Admissions', path: '/admissions' },
    ];

    const moreNavItems = [
        { name: 'Infrastructure', path: '/infrastructure' },
        { name: 'Activities', path: '/activities' },
        { name: 'Gallery', path: '/gallery' },
        { name: 'Teachers', path: '/teachers' },
        { name: 'News', path: '/news' },
        { name: 'Resources', path: '/resources' },
    ];

    const isMoreActive = moreNavItems.some(item => location.pathname === item.path);

    return (
        <header
            className={`fixed top-10 left-0 right-0 z-50 transition-all duration-300 ${
                isScrolled ? 'shadow-2xl' : 'shadow-lg'
            }`}
            style={{
                background: 'linear-gradient(90deg, #C4122F 0%, #7C2D92 50%, #1E3A8A 100%)'
            }}
            data-testid="header"
        >
            <div className="container mx-auto px-4 md:px-8">
                <div className="flex items-center justify-between py-3">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
                        <img
                            src="https://customer-assets.emergentagent.com/job_rkenglish-dynamic/artifacts/znfm787q_R.K._Eng._High_School_Logo-removebg-preview.png"
                            alt="R.K. English High School Logo"
                            className="h-14 md:h-20 transition-transform group-hover:scale-105"
                        />
                        <div className="hidden md:block">
                            <h1 className="font-outfit font-bold text-xl leading-tight text-white">
                                R.K. English High School
                            </h1>
                            <p className="text-xs text-white/80 font-medium">Where Excellence Meets Education</p>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center gap-2" data-testid="desktop-nav">
                        {mainNavItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`relative font-outfit font-semibold text-sm px-4 py-2 rounded-lg transition-all duration-300 ${
                                    location.pathname === item.path
                                        ? 'text-white bg-white/20'
                                        : 'text-white/90 hover:text-white hover:bg-white/10'
                                }`}
                                data-testid={`nav-link-${item.name.toLowerCase()}`}
                            >
                                {item.name}
                                {location.pathname === item.path && (
                                    <motion.div
                                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-white"
                                        layoutId="underline"
                                        initial={false}
                                        transition={{ duration: 0.3 }}
                                    />
                                )}
                            </Link>
                        ))}

                        {/* More Dropdown */}
                        <div 
                            className="relative"
                            onMouseEnter={() => setIsMoreDropdownOpen(true)}
                            onMouseLeave={() => setIsMoreDropdownOpen(false)}
                        >
                            <button
                                className={`relative font-outfit font-semibold text-sm px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-1 ${
                                    isMoreActive
                                        ? 'text-white bg-white/20'
                                        : 'text-white/90 hover:text-white hover:bg-white/10'
                                }`}
                                data-testid="nav-more-dropdown"
                            >
                                More
                                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isMoreDropdownOpen ? 'rotate-180' : ''}`} />
                                {isMoreActive && (
                                    <motion.div
                                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-white"
                                        layoutId="underline"
                                    />
                                )}
                            </button>

                            <AnimatePresence>
                                {isMoreDropdownOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                                        className="absolute top-full mt-2 right-0 w-56 bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-100"
                                        data-testid="more-dropdown-menu"
                                    >
                                        {moreNavItems.map((item, index) => (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                className={`block px-6 py-3 font-outfit font-medium text-sm transition-all duration-200 ${
                                                    location.pathname === item.path
                                                        ? 'bg-red-600 text-white'
                                                        : 'text-slate-700 hover:bg-red-50 hover:text-red-600'
                                                } ${index !== moreNavItems.length - 1 ? 'border-b border-slate-100' : ''}`}
                                                data-testid={`dropdown-link-${item.name.toLowerCase()}`}
                                            >
                                                {item.name}
                                            </Link>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <Link
                            to="/contact"
                            className={`relative font-outfit font-semibold text-sm px-4 py-2 rounded-lg transition-all duration-300 ${
                                location.pathname === '/contact'
                                    ? 'text-white bg-white/20'
                                    : 'text-white/90 hover:text-white hover:bg-white/10'
                            }`}
                            data-testid="nav-link-contact"
                        >
                            Contact
                            {location.pathname === '/contact' && (
                                <motion.div
                                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-white"
                                    layoutId="underline"
                                />
                            )}
                        </Link>
                    </nav>

                    {/* Admission CTA */}
                    <Link
                        to="/admissions"
                        className="hidden md:inline-flex items-center justify-center px-6 py-3 bg-white hover:bg-gray-100 text-red-600 font-outfit font-bold text-sm rounded-full shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 hover:-translate-y-0.5"
                        data-testid="admission-cta-button"
                    >
                        Admissions Open
                    </Link>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors text-white"
                        data-testid="mobile-menu-button"
                        aria-label="Toggle menu"
                    >
                        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="lg:hidden bg-white/95 backdrop-blur-lg overflow-hidden"
                        data-testid="mobile-menu"
                    >
                        <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
                            {mainNavItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`font-outfit font-medium px-4 py-3 rounded-lg transition-all ${
                                        location.pathname === item.path
                                            ? 'bg-red-600 text-white'
                                            : 'hover:bg-red-50 text-slate-700'
                                    }`}
                                    data-testid={`mobile-nav-link-${item.name.toLowerCase()}`}
                                >
                                    {item.name}
                                </Link>
                            ))}
                            
                            <div className="border-t border-slate-200 my-2"></div>
                            <p className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">More</p>
                            
                            {moreNavItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`font-outfit font-medium px-4 py-3 rounded-lg transition-all ${
                                        location.pathname === item.path
                                            ? 'bg-red-600 text-white'
                                            : 'hover:bg-red-50 text-slate-700'
                                    }`}
                                    data-testid={`mobile-nav-link-${item.name.toLowerCase()}`}
                                >
                                    {item.name}
                                </Link>
                            ))}
                            
                            <Link
                                to="/contact"
                                className={`font-outfit font-medium px-4 py-3 rounded-lg transition-all ${
                                    location.pathname === '/contact'
                                        ? 'bg-red-600 text-white'
                                        : 'hover:bg-red-50 text-slate-700'
                                }`}
                                data-testid="mobile-nav-link-contact"
                            >
                                Contact
                            </Link>
                            
                            <Link
                                to="/admissions"
                                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-outfit font-bold px-4 py-3 rounded-lg text-center mt-2 transition-all transform hover:scale-105"
                                data-testid="mobile-admission-cta"
                            >
                                Admissions Open
                            </Link>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
